import { useEffect, useState, type Key } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Table, Input, InputNumber, Select, Button, Tag, Modal, Segmented, Space, Typography,
  Form, Row, Col, Card, Statistic, Alert, App as AntApp, Flex, DatePicker, Checkbox,
} from 'antd';
import {
  PlusOutlined, EyeOutlined, DeleteOutlined, CheckCircleFilled, CheckCircleOutlined,
  EditOutlined, UndoOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { API_URL } from '../config';
import { hojeIso } from '../utils/dataFmt';
import { usePerms } from '../hooks/usePerms';
import { PROPS_DINHEIRO_BR } from '../utils/dinheiroInput';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

const fmt = (v: number) =>
  'R$ ' + (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (s: string | null) => {
  if (!s) return '—';
  const [y, m, d] = s.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

interface Emprestimo {
  id: string;
  instituicao: string;
  identificador: string | null;
  valor_principal: string;
  data_contratacao: string;
  prazo_meses: number;
  taxa_mensal_pct: string;
  sistema: 'PRICE' | 'SAC' | 'CONSORCIO';
  carencia_meses: number;
  iof_total: string;
  observacoes: string | null;
  data_quitacao: string | null;
  status: 'ativo' | 'quitado';
  tipo_contrato: string;
  taxa_label: string;
  indice_mensal_pct: string;
  custos_iniciais: string;
  taxa_seguro_mensal_pct: string;
  cronograma_origem: 'calculado' | 'importado';
  juros_total: string;
  juros_pagos: string;
  principal_pago: string;
  saldo_devedor_atual: string;
  total_parcelas: number;
  parcelas_pagas: number;
}

const TIPOS_CONTRATO = [
  { value: 'emprestimo', label: 'Empréstimo bancário', taxaLabel: 'Juros', sistema: 'PRICE' },
  { value: 'mutua', label: 'Mútuo / Empréstimo com índice', taxaLabel: 'Juros', sistema: 'PRICE' },
  { value: 'financiamento', label: 'Financiamento (veículo/imóvel)', taxaLabel: 'Juros', sistema: 'PRICE' },
  { value: 'consorcio', label: 'Consórcio', taxaLabel: 'Taxa de administração', sistema: 'CONSORCIO' },
];

interface Parcela {
  id: string;
  numero: number;
  data_vencimento: string;
  valor_juros: string;
  valor_principal: string;
  valor_parcela: string;
  saldo_devedor: string;
  paga: boolean;
  data_pagamento: string | null;
  // Pagamento registrado com valor real (15/10/2026)
  valor_pago?: string | null;
  pagamento_id?: string | null;
  pagamento_data?: string | null;
  pagamento_valor?: string | null;
  pagamento_nominal?: string | null;
  pagamento_juros?: string | null;
  pagamento_meses?: number | null;
  lancamento_pagar_id?: string | null;
  pagamento_de?: number | null;
  pagamento_ate?: number | null;
  pagamento_n?: number | null;
  despesa_conciliada?: boolean | null;
}

type StatusFiltro = 'ativos' | 'quitados' | 'todos';

function EmprestimosInner() {
  const { message, modal } = AntApp.useApp();
  const [emps, setEmps] = useState<Emprestimo[]>([]);
  const [loading, setLoading] = useState(true);
  // Os codigos financeiro.emprestimos.editar/.excluir existem no catalogo desde
  // a migration 1779897530993 e nenhuma tela os usava — a pagina inteira nao
  // tinha gate. 🚨 Falha ao CARREGAR a matriz nao pode virar "voce nao pode"
  // (incidente de 01/09): o gate de verdade e o do servidor, entao aqui erro
  // deixa passar.
  const { pode, erro: permsErro } = usePerms();
  const podeEditar = permsErro || pode('financeiro.emprestimos.editar') || pode('admin.permissoes.editar');
  const podeExcluir = permsErro || pode('financeiro.emprestimos.excluir') || pode('admin.permissoes.editar');
  const [filtroStatus, setFiltroStatus] = useState<StatusFiltro>('ativos');
  const [mostrarNovo, setMostrarNovo] = useState(false);
  const [verParcelas, setVerParcelas] = useState<Emprestimo | null>(null);
  // Pedido do Lucas (08/09/2026): "preciso conseguir editar o empréstimo
  // contratado". O mesmo formulário serve para criar e para editar.
  const [editando, setEditando] = useState<Emprestimo | null>(null);

  useEffect(() => { carregar(); }, [filtroStatus]);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const qs = filtroStatus === 'todos' ? '' : `?status=${filtroStatus}`;
      const res = await axios.get(`${API_URL}/emprestimos${qs}`, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setEmps(data);
    } finally { setLoading(false); }
  }

  function excluir(id: string) {
    modal.confirm({
      title: 'Excluir empréstimo?',
      content: 'Esta ação remove também todas as parcelas associadas.',
      okText: 'Excluir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.delete(`${API_URL}/emprestimos/${id}`, { headers: { Authorization: token } });
          message.success('Empréstimo excluído.');
          await carregar();
        } catch (e: any) {
          // Com pagamento registrado o servidor recusa e diz por quê (15/10/2026).
          const d = e?.response?.data;
          const corpo = d?.body ? (() => { try { return JSON.parse(d.body); } catch { return d; } })() : d;
          message.error(corpo?.error || ('Erro: ' + e.message), 10);
        }
      },
    });
  }

  const totais = emps.reduce((acc, e) => {
    acc.emprestado += parseFloat(e.valor_principal);
    acc.saldo_devedor += parseFloat(e.saldo_devedor_atual);
    acc.juros_totais += parseFloat(e.juros_total);
    acc.juros_pagos += parseFloat(e.juros_pagos);
    return acc;
  }, { emprestado: 0, saldo_devedor: 0, juros_totais: 0, juros_pagos: 0 });

  const columns: ColumnsType<Emprestimo> = [
    {
      title: 'Instituição / Contrato', dataIndex: 'instituicao', key: 'inst',
      render: (v, r) => (
        <>
          <Text strong style={{ color: '#020D7F' }}>{v}</Text>
          {r.identificador && <div style={{ fontSize: 10, color: '#888' }}>{r.identificador}</div>}
        </>
      ),
    },
    {
      title: 'Contratação', dataIndex: 'data_contratacao', key: 'contr', width: 100, align: 'center',
      render: (v) => <Text type="secondary" style={{ fontSize: 11 }}>{fmtDate(v)}</Text>,
    },
    {
      title: 'Principal', dataIndex: 'valor_principal', key: 'principal', width: 120, align: 'right',
      render: (v) => fmt(parseFloat(v)),
    },
    {
      title: 'Prazo', dataIndex: 'prazo_meses', key: 'prazo', width: 70, align: 'center',
      render: (v) => `${v}m`,
    },
    {
      title: 'Taxa', dataIndex: 'taxa_mensal_pct', key: 'taxa', width: 70, align: 'center',
      render: (v) => `${parseFloat(v).toFixed(3)}%`,
    },
    {
      title: 'Sist.', dataIndex: 'sistema', key: 'sist', width: 70, align: 'center',
      render: (v) => <Text style={{ fontSize: 11 }}>{v}</Text>,
    },
    {
      title: 'Parcelas', key: 'parc', width: 90, align: 'center',
      render: (_, r) => <Text style={{ fontSize: 11 }}>{r.parcelas_pagas} / {r.total_parcelas}</Text>,
    },
    {
      title: 'Saldo dev.', dataIndex: 'saldo_devedor_atual', key: 'saldo', width: 120, align: 'right',
      render: (v) => <Text strong style={{ color: '#B91626' }}>{fmt(parseFloat(v))}</Text>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 90, align: 'center',
      render: (s: string) => s === 'quitado'
        ? <Tag color="default" style={{ margin: 0 }}>QUITADO</Tag>
        : <Tag color="success" style={{ margin: 0 }}>Ativo</Tag>,
    },
    {
      title: 'Ações', key: 'acoes', width: 220, align: 'center',
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => setVerParcelas(r)}>
            Parcelas
          </Button>
          {podeEditar && (
            <Button size="small" icon={<EditOutlined />} onClick={() => setEditando(r)}>
              Editar
            </Button>
          )}
          {podeExcluir && (
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => excluir(r.id)} />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px 24px' }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <Title level={4} style={{ margin: 0, color: '#020D7F' }}>
            Empréstimos e Financiamentos
          </Title>
          <Space wrap>
            <Segmented
              value={filtroStatus}
              onChange={(v) => setFiltroStatus(v as StatusFiltro)}
              options={[
                { label: 'Ativos', value: 'ativos' },
                { label: 'Quitados', value: 'quitados' },
                { label: 'Todos', value: 'todos' },
              ]}
              size="small"
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setMostrarNovo(true)}>
              Novo empréstimo
            </Button>
          </Space>
        </Flex>

        <Row gutter={10}>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Total emprestado"
                value={totais.emprestado}
                prefix="R$"
                precision={2}
                valueStyle={{ color: '#020D7F', fontSize: 18 }}
              />
              <Text type="secondary" style={{ fontSize: 10 }}>{emps.length} contratos</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Saldo devedor atual"
                value={totais.saldo_devedor}
                prefix="R$"
                precision={2}
                valueStyle={{ color: '#B91626', fontSize: 18 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Juros totais contratados"
                value={totais.juros_totais}
                prefix="R$"
                precision={2}
                valueStyle={{ color: '#B07500', fontSize: 18 }}
              />
              <Text type="secondary" style={{ fontSize: 10 }}>Pagos: {fmt(totais.juros_pagos)}</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Juros a pagar"
                value={totais.juros_totais - totais.juros_pagos}
                prefix="R$"
                precision={2}
                valueStyle={{ color: '#B07500', fontSize: 18 }}
              />
            </Card>
          </Col>
        </Row>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={emps}
          loading={loading}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true }}
          rowClassName={(r) => r.status === 'quitado' ? 'row-fechado' : ''}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'Nenhum empréstimo neste filtro.' }}
        />
      </Space>

      {mostrarNovo && (
        <FormNovo onClose={() => setMostrarNovo(false)} onSaved={async () => { setMostrarNovo(false); await carregar(); }} />
      )}
      {editando && (
        <FormNovo
          emp={editando}
          onClose={() => setEditando(null)}
          onSaved={async () => { setEditando(null); await carregar(); }}
        />
      )}
      {verParcelas && (
        <ModalParcelas emp={verParcelas} onClose={() => setVerParcelas(null)} onChanged={carregar} />
      )}
    </div>
  );
}

// Rotulo e equivalente anual da taxa — fora do JSX so pra o formulario nao
// carregar template literal no meio da arvore.
const TAXA_LABEL = (f: any) => `${f.taxa_label} mensal (%)`;
const EQUIV_ANUAL = (pct: any) =>
  `Equivalente anual: ${pct ? (((1 + parseFloat(pct) / 100) ** 12 - 1) * 100).toFixed(2) : 0}%`;

function FormNovo({ onClose, onSaved, emp }: { onClose: () => void; onSaved: () => void; emp?: Emprestimo }) {
  const { message } = AntApp.useApp();
  const editando = !!emp;
  // Cronograma que veio do banco não se recalcula pela fórmula: os campos que
  // definiriam a tabela ficam travados, e a tela diz por quê. Quem manda é o
  // papel do banco (docs/emprestimos.md).
  const cronogramaDoBanco = emp?.cronograma_origem === 'importado';
  const temParcelaPaga = (emp?.parcelas_pagas || 0) > 0;
  const travaCronograma = cronogramaDoBanco || temParcelaPaga;
  const [form, setForm] = useState<any>(emp ? {
    tipo_contrato: emp.tipo_contrato || 'emprestimo',
    taxa_label: emp.taxa_label || 'Juros',
    instituicao: emp.instituicao || '',
    identificador: emp.identificador || '',
    valor_principal: emp.valor_principal,
    data_contratacao: String(emp.data_contratacao || '').slice(0, 10),
    prazo_meses: emp.prazo_meses,
    taxa_mensal_pct: emp.taxa_mensal_pct,
    indice_mensal_pct: emp.indice_mensal_pct,
    taxa_seguro_mensal_pct: emp.taxa_seguro_mensal_pct,
    custos_iniciais: emp.custos_iniciais,
    sistema: emp.sistema,
    carencia_meses: emp.carencia_meses,
    iof_total: emp.iof_total,
    observacoes: emp.observacoes || '',
  } : {
    tipo_contrato: 'emprestimo',
    taxa_label: 'Juros',
    instituicao: '',
    identificador: '',
    valor_principal: '',
    data_contratacao: hojeIso(),
    prazo_meses: 36,
    taxa_mensal_pct: '1.5',
    indice_mensal_pct: 0,
    taxa_seguro_mensal_pct: 0,
    custos_iniciais: 0,
    sistema: 'PRICE',
    carencia_meses: 0,
    iof_total: 0,
    observacoes: '',
  });

  function aplicarTipoContrato(tipo: string) {
    const t = TIPOS_CONTRATO.find(x => x.value === tipo);
    if (!t) return;
    setForm((f: any) => ({ ...f, tipo_contrato: tipo, taxa_label: t.taxaLabel, sistema: t.sistema }));
  }

  const [preview, setPreview] = useState<any[] | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function gerarPreview() {
    if (!form.valor_principal || !form.prazo_meses || !form.taxa_mensal_pct) return;
    // Editando um cronograma travado, a prévia da fórmula seria uma tabela que
    // NÃO é a do contrato — pior que nenhuma.
    if (travaCronograma) return;
    try {
      const token = await getToken();
      const res = await axios.post(`${API_URL}/emprestimos/preview`, {
        valor_principal: parseFloat(form.valor_principal),
        prazo_meses: parseInt(form.prazo_meses),
        taxa_mensal_pct: parseFloat(form.taxa_mensal_pct),
        indice_mensal_pct: parseFloat(form.indice_mensal_pct) || 0,
        taxa_seguro_mensal_pct: parseFloat(form.taxa_seguro_mensal_pct) || 0,
        sistema: form.sistema,
        carencia_meses: parseInt(form.carencia_meses) || 0,
        data_contratacao: form.data_contratacao,
      }, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setPreview(data.parcelas);
    } catch {}
  }

  useEffect(() => {
    const t = setTimeout(gerarPreview, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [form.valor_principal, form.prazo_meses, form.taxa_mensal_pct, form.indice_mensal_pct, form.taxa_seguro_mensal_pct, form.sistema, form.carencia_meses, form.data_contratacao]);

  const totais = preview ? preview.reduce((a, p) => ({
    juros: a.juros + p.juros,
    principal: a.principal + p.principal,
    parcela_sum: a.parcela_sum + p.parcela,
  }), { juros: 0, principal: 0, parcela_sum: 0 }) : null;

  async function salvar() {
    if (!form.instituicao || !form.valor_principal || !form.prazo_meses || !form.taxa_mensal_pct) {
      message.warning('Preencha os campos obrigatórios');
      return;
    }
    setSalvando(true);
    try {
      const token = await getToken();
      const corpo = {
        ...form,
        valor_principal: parseFloat(form.valor_principal),
        prazo_meses: parseInt(form.prazo_meses),
        taxa_mensal_pct: parseFloat(form.taxa_mensal_pct),
        indice_mensal_pct: parseFloat(form.indice_mensal_pct) || 0,
        taxa_seguro_mensal_pct: parseFloat(form.taxa_seguro_mensal_pct) || 0,
        custos_iniciais: parseFloat(form.custos_iniciais) || 0,
        carencia_meses: parseInt(form.carencia_meses) || 0,
        iof_total: parseFloat(form.iof_total) || 0,
      };
      if (editando) {
        await axios.put(`${API_URL}/emprestimos/${emp!.id}`, corpo, { headers: { Authorization: token } });
      } else {
        await axios.post(`${API_URL}/emprestimos`, corpo, { headers: { Authorization: token } });
      }
      message.success(editando ? 'Empréstimo atualizado.' : 'Empréstimo criado.');
      onSaved();
    } catch (e: any) {
      // O servidor recusa com FRASE (parcela já paga, cronograma do banco,
      // tabela que não fecha). Mostrar `e.message` engoliria tudo isso num
      // "Request failed with status code 409".
      const d = e.response?.data;
      const corpo = d?.body ? (() => { try { return JSON.parse(d.body); } catch { return d; } })() : d;
      message.error(corpo?.error || e.message, 8);
    }
    finally { setSalvando(false); }
  }

  return (
    <Modal
      open
      onCancel={onClose}
      onOk={salvar}
      okText={editando ? 'Salvar alterações' : 'Criar empréstimo'}
      cancelText="Cancelar"
      confirmLoading={salvando}
      title={editando ? `Editar empréstimo · ${emp!.instituicao}` : 'Novo Empréstimo'}
      // Regra da casa (CLAUDE.md): modal não obriga a rolar tendo tela sobrando.
      // Era width={900} com 13 campos empilhados de dois em dois e o cronograma
      // embaixo — nove andares, e o botão de criar caía fora da dobra num
      // notebook (relato do Lucas, 08/09/2026). Agora o contrato e o cronograma
      // ficam LADO A LADO e o corpo só rola no que passar da tela.
      width="94vw"
      style={{ maxWidth: 1280, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' } }}
      destroyOnClose
    >
      <Form layout="vertical" size="small">
        <Row gutter={24}>
        {/* ——— Coluna A: o contrato ——— */}
        <Col xs={24} lg={13}>
        <Row gutter={12}>
          <Col xs={24} sm={12} xl={8}>
            <Form.Item
              label="Tipo de contrato"
              required
              extra={
                <>
                  {form.tipo_contrato === 'consorcio' && 'Consórcio: amortização linear + taxa de administração mensal.'}
                  {form.tipo_contrato === 'mutua' && 'Mútuo: taxa de juros + índice adicional.'}
                </>
              }
            >
              <Select
                value={form.tipo_contrato}
                onChange={aplicarTipoContrato}
                options={TIPOS_CONTRATO.map(t => ({ value: t.value, label: t.label }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <Form.Item label="Sistema" required>
              <Select
                disabled={travaCronograma}
                value={form.sistema}
                onChange={(v) => setForm({ ...form, sistema: v })}
                options={[
                  { value: 'PRICE', label: 'PRICE (parcelas fixas)' },
                  { value: 'SAC', label: 'SAC (amortização constante)' },
                  { value: 'CONSORCIO', label: 'CONSÓRCIO (amortização linear)' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <Form.Item label="Data de contratação" required>
              <DatePicker
                disabled={travaCronograma}
                value={form.data_contratacao ? dayjs(form.data_contratacao) : null}
                onChange={(d: Dayjs | null) => setForm({ ...form, data_contratacao: d ? d.format('YYYY-MM-DD') : '' })}
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} xl={16}>
            <Form.Item label="Instituição" required>
              <Input
                value={form.instituicao}
                onChange={e => setForm({ ...form, instituicao: e.target.value })}
                placeholder="Ex.: Banco do Brasil / Mútua / Embracon"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <Form.Item label="Identificador (contrato)">
              <Input
                value={form.identificador}
                onChange={e => setForm({ ...form, identificador: e.target.value })}
                placeholder="Ex.: 12345-6"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} xl={8}>
            <Form.Item label="Valor principal" required>
              <InputNumber {...PROPS_DINHEIRO_BR}
                disabled={travaCronograma}
                value={form.valor_principal}
                onChange={(v) => setForm({ ...form, valor_principal: v })}
                style={{ width: '100%' }}
                prefix="R$"
                precision={2}
                min={0}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <Form.Item label="Prazo (meses)" required>
              <InputNumber
                disabled={travaCronograma}
                value={form.prazo_meses}
                onChange={(v) => setForm({ ...form, prazo_meses: v })}
                style={{ width: '100%' }}
                min={1}
                max={600}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <Form.Item
              label={TAXA_LABEL(form)}
              required
              extra={
                form.tipo_contrato === 'consorcio'
                  ? 'Taxa administrativa mensal do consórcio'
                  : EQUIV_ANUAL(form.taxa_mensal_pct)
              }
            >
              <InputNumber
                disabled={travaCronograma}
                value={form.taxa_mensal_pct}
                onChange={(v) => setForm({ ...form, taxa_mensal_pct: v })}
                style={{ width: '100%' }}
                step={0.001}
                min={0}
                suffix="%"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} xl={8}>
            <Form.Item label="Carência (meses só-encargo)">
              <InputNumber
                disabled={travaCronograma}
                value={form.carencia_meses}
                onChange={(v) => setForm({ ...form, carencia_meses: v })}
                style={{ width: '100%' }}
                min={0}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <Form.Item label="Índice mensal (%)" extra="IPCA/TR/CDI · fundo de reserva no consórcio">
              <InputNumber
                disabled={travaCronograma}
                value={form.indice_mensal_pct}
                onChange={(v) => setForm({ ...form, indice_mensal_pct: v })}
                style={{ width: '100%' }}
                step={0.001}
                min={0}
                suffix="%"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <Form.Item label="Seguro mensal (%)" extra="Prestamista/quebra de garantia (opcional)">
              <InputNumber
                disabled={travaCronograma}
                value={form.taxa_seguro_mensal_pct}
                onChange={(v) => setForm({ ...form, taxa_seguro_mensal_pct: v })}
                style={{ width: '100%' }}
                step={0.001}
                min={0}
                suffix="%"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} xl={8}>
            <Form.Item
              label="Custos iniciais (R$)"
              extra="Taxa de adesão / cadastro · lançados como despesa na data de contratação"
            >
              <InputNumber {...PROPS_DINHEIRO_BR}
                value={form.custos_iniciais}
                onChange={(v) => setForm({ ...form, custos_iniciais: v })}
                style={{ width: '100%' }}
                precision={2}
                min={0}
                prefix="R$"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <Form.Item label="IOF total (R$)" extra="Entra na DRE junto dos custos iniciais">
              <InputNumber {...PROPS_DINHEIRO_BR}
                value={form.iof_total}
                onChange={(v) => setForm({ ...form, iof_total: v })}
                style={{ width: '100%' }}
                precision={2}
                min={0}
                prefix="R$"
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label="Observações" style={{ marginBottom: 8 }}>
              <Input.TextArea
                value={form.observacoes}
                onChange={e => setForm({ ...form, observacoes: e.target.value })}
                rows={2}
              />
            </Form.Item>
          </Col>
        </Row>

        <Alert
          type="warning"
          showIcon
          message={
            <Text style={{ fontSize: 11 }}>
              Somente a <strong>parcela de juros</strong> entra na DRE (Despesas Financeiras). O principal reduz o passivo (não-despesa).
              Lembre-se de marcar a categoria "Empréstimos e Dívidas" do ContaAzul como <strong>ignorada</strong> para evitar dupla contagem.
            </Text>
          }
        />
        </Col>

        {/* ——— Coluna B: o cronograma que o sistema propõe ——— */}
        <Col xs={24} lg={11}>
          {editando && travaCronograma ? (
            // Editando um contrato que não pode ter o cronograma recalculado:
            // a prévia da fórmula seria uma tabela que NÃO é a do contrato.
            // Em vez dela, o retrato do cronograma que existe + o porquê da trava.
            <>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 10 }}
                message={
                  <Text style={{ fontSize: 11.5 }}>
                    {cronogramaDoBanco
                      ? 'O cronograma deste contrato veio do banco. Valor, prazo, taxa, sistema, carência e data de contratação ficam travados aqui — recalcular pela fórmula jogaria fora a tabela verdadeira. Para atualizar as parcelas (reajuste de pós-fixado, por exemplo), reimporte a tabela do banco.'
                      : 'Há parcela já marcada como paga. Valor, prazo, taxa, sistema, carência e data de contratação ficam travados — mexer neles refaria o cronograma inteiro e reescreveria o que já foi pago. Desmarque a baixa em "Parcelas" se ela estiver errada.'}
                  </Text>
                }
              />
              <Card size="small" title={<Text style={{ fontSize: 12 }}>Cronograma atual</Text>}>
                <Row gutter={8}>
                  <Col span={12}>
                    <Statistic title="Parcelas" value={emp!.total_parcelas} valueStyle={{ fontSize: 14 }} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="Já pagas" value={emp!.parcelas_pagas} valueStyle={{ fontSize: 14 }} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="Juros contratados" value={parseFloat(emp!.juros_total)} precision={2} prefix="R$" valueStyle={{ fontSize: 14, color: '#B07500' }} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="Saldo devedor" value={parseFloat(emp!.saldo_devedor_atual)} precision={2} prefix="R$" valueStyle={{ fontSize: 14 }} />
                  </Col>
                </Row>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Nome do banco, número do contrato, custos iniciais, IOF e observações continuam editáveis — nenhum deles mexe nas parcelas.
                </Text>
              </Card>
            </>
          ) : totais ? (
            <>
              <Card size="small" style={{ marginBottom: 10 }}>
                <Row gutter={8}>
                  <Col span={12}>
                    <Statistic title="Total a pagar" value={totais.parcela_sum} precision={2} prefix="R$" valueStyle={{ fontSize: 14 }} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="Juros total" value={totais.juros} precision={2} prefix="R$" valueStyle={{ fontSize: 14, color: '#B07500' }} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="Principal total" value={totais.principal} precision={2} prefix="R$" valueStyle={{ fontSize: 14 }} />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Custo efetivo"
                      value={(totais.parcela_sum / parseFloat(form.valor_principal) - 1) * 100}
                      precision={1}
                      suffix="%"
                      valueStyle={{ fontSize: 14 }}
                    />
                  </Col>
                </Row>
              </Card>

              {editando && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 10 }}
                  message={
                    <Text style={{ fontSize: 11.5 }}>
                      Ao salvar, as <strong>parcelas em aberto</strong> deste empréstimo são refeitas por este cronograma.
                    </Text>
                  }
                />
              )}
              {preview && preview.length > 0 && (
                <Card
                  size="small"
                  title={<Text style={{ fontSize: 12 }}>Cronograma proposto · {preview.length} parcelas</Text>}
                  styles={{ body: { padding: 0 } }}
                >
                  {/* Altura amarrada à viewport (regra da casa): usa o monitor
                      grande em vez de um teto fixo de 260px. */}
                  <div style={{ maxHeight: 'clamp(200px, calc(100vh - 430px), 620px)', overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, fontFamily: 'monospace' }}>
                      <thead>
                        <tr style={{ background: '#FAFBFC', position: 'sticky', top: 0 }}>
                          <th style={{ padding: 6, textAlign: 'center' }}>#</th>
                          <th style={{ padding: 6, textAlign: 'center' }}>Vencimento</th>
                          <th style={{ padding: 6, textAlign: 'right' }}>Juros</th>
                          <th style={{ padding: 6, textAlign: 'right' }}>Principal</th>
                          <th style={{ padding: 6, textAlign: 'right' }}>Parcela</th>
                          <th style={{ padding: 6, textAlign: 'right' }}>Saldo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.map(p => (
                          <tr key={p.numero}>
                            <td style={{ padding: 4, textAlign: 'center', color: '#666' }}>{p.numero}</td>
                            <td style={{ padding: 4, textAlign: 'center', color: '#666' }}>{fmtDate(p.data_vencimento)}</td>
                            <td style={{ padding: 4, textAlign: 'right', color: '#B07500' }}>{fmt(p.juros)}</td>
                            <td style={{ padding: 4, textAlign: 'right' }}>{fmt(p.principal)}</td>
                            <td style={{ padding: 4, textAlign: 'right', fontWeight: 500 }}>{fmt(p.parcela)}</td>
                            <td style={{ padding: 4, textAlign: 'right', color: '#888' }}>{fmt(p.saldo)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </>
          ) : (
            // Antes o lado direito ficava simplesmente vazio e ninguém sabia por
            // quê — a prévia só nasce com valor, prazo e taxa preenchidos.
            <Card size="small" style={{ textAlign: 'center', padding: '28px 12px' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Preencha <strong>valor principal</strong>, <strong>prazo</strong> e a <strong>taxa</strong> para
                o sistema montar o cronograma aqui.
              </Text>
            </Card>
          )}
        </Col>
        </Row>
      </Form>
    </Modal>
  );
}

// ═══ Pagamento com valor real (15/10/2026) ══════════════════════════════════
// Pedido do Lucas: lançar o pagamento no módulo e a despesa nascer sozinha no
// Contas a Pagar, na categoria certa. Caso real: as 12 últimas parcelas do
// Pronampe do Itaú pagas por boleto, pela conta do Inter, por R$ 101.774,83 —
// menos que os R$ 126.420,72 do cronograma (antecipação com desconto). A conta
// abaixo é só a PRÉVIA da tela; quem grava e decide é o servidor
// (dre-engine/handlers/emprestimosPagamentos.js).
const n2 = (v: any) => parseFloat(v) || 0;
const r2 = (v: number) => Math.round(v * 100) / 100;
function faixaParcelas(nums: number[]) {
  const o = [...nums].sort((a, b) => a - b);
  if (o.length === 1) return String(o[0]);
  const seguidas = o.every((n, i) => i === 0 || n === o[i - 1] + 1);
  return seguidas ? `${o[0]} a ${o[o.length - 1]}` : o.join(', ');
}
const mesesEntre = (de: string, ate: string) => {
  const [y1, m1] = de.split('-').map(Number);
  const [y2, m2] = ate.split('-').map(Number);
  return (y2 * 12 + m2) - (y1 * 12 + m1) + 1;
};
const mesAno = (iso: string) => `${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const categoriaDoPagamento = (tipo: string) =>
  (tipo === 'emprestimo' || tipo === 'financiamento') ? 'Empréstimos de Bancos' : 'Empréstimos de Outras Instituições';
const erroDoServidor = (e: any, padrao: string) => {
  const d = e?.response?.data;
  const corpo = d?.body ? (() => { try { return JSON.parse(d.body); } catch { return d; } })() : d;
  return corpo?.error || padrao;
};

interface ContaFin { id: string; nome: string; apelido?: string | null; tipo: string; ativo?: boolean }

function ModalRegistrarPagamento({ emp, selecionadas, abertasRestantes, onClose, onSaved }: {
  emp: Emprestimo; selecionadas: Parcela[]; abertasRestantes: Parcela[];
  onClose: () => void; onSaved: () => void;
}) {
  const { message, modal } = AntApp.useApp();
  const sel = [...selecionadas].sort((a, b) => a.numero - b.numero);
  const nums = sel.map(p => p.numero);
  const nominal = r2(sel.reduce((a, p) => a + n2(p.valor_parcela), 0));
  const principal = r2(sel.reduce((a, p) => a + n2(p.valor_principal), 0));
  const jurosCarencia = r2(sel.filter(p => n2(p.valor_principal) === 0).reduce((a, p) => a + n2(p.valor_juros), 0));
  const jurosCronograma = r2(nominal - principal);

  const [valor, setValor] = useState<number | null>(nominal);
  const [data, setData] = useState<string>(hojeIso());
  const [contaId, setContaId] = useState<string | undefined>(undefined);
  const [forma, setForma] = useState<string | undefined>(undefined);
  const [criarDespesa, setCriarDespesa] = useState(true);
  const [contas, setContas] = useState<ContaFin[]>([]);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${API_URL}/financeiro/contas`, { headers: { Authorization: token } });
        const d = res.data?.body ? JSON.parse(res.data.body) : res.data;
        const lista: ContaFin[] = Array.isArray(d) ? d : (d?.contas || []);
        setContas(lista.filter(c => c.tipo === 'corrente' && c.ativo !== false));
      } catch { setContas([]); }
    })();
  }, []);

  const pago = r2(valor || 0);
  const dif = r2(pago - nominal);
  const juros = r2(pago - principal - jurosCarencia);
  const menorVenc = sel.map(p => String(p.data_vencimento).slice(0, 10)).sort()[0];
  const comp = `${((menorVenc && menorVenc < data) ? menorVenc : data).slice(0, 7)}-01`;
  const ultimoAberto = abertasRestantes.map(p => String(p.data_vencimento).slice(0, 10)).sort().pop();
  const meses = juros < 0 && ultimoAberto ? Math.max(1, mesesEntre(comp, `${ultimoAberto.slice(0, 7)}-01`)) : 1;
  const fimDesconto = (() => {
    const [y, m] = comp.split('-').map(Number);
    const t = y * 12 + (m - 1) + (meses - 1);
    return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}-01`;
  })();
  const conta = contas.find(c => c.id === contaId);
  const cat = categoriaDoPagamento(emp.tipo_contrato);
  const longeDemais = pago > 0 && (pago > nominal * 3 || pago < nominal * 0.2);
  const podeSalvar = pago > 0 && !!data && !longeDemais && (!criarDespesa || !!contaId);

  async function salvar(criar: boolean = criarDespesa) {
    setSalvando(true);
    try {
      const token = await getToken();
      const res = await axios.post(`${API_URL}/emprestimos/${emp.id}/parcelas/${nums[0]}/pagar`, {
        parcelas: nums, valor_pago: pago, data_pagamento: data,
        conta_financeira_id: contaId, forma_pagamento: forma, criar_despesa: criar,
      }, { headers: { Authorization: token } });
      const d = res.data?.body ? JSON.parse(res.data.body) : res.data;
      message.success(`Pagamento registrado: parcelas ${faixaParcelas(nums)}, ${fmt(pago)}.`
        + (d?.lancamento_pagar_id ? ' A despesa está no Contas a Pagar — agora concilie a linha do extrato.'
          : ' Nenhuma despesa nova: a que já existe no Contas a Pagar é a que a conciliação vai usar.'), 8);
      onSaved();
    } catch (e: any) {
      // A despesa desse dinheiro já existe (a parcela que veio do Conta Azul, ou o
      // boleto lançado à mão): o servidor recusa e lista. Criar outra contaria a
      // saída duas vezes — a saída é registrar usando a que existe.
      const dd = e?.response?.data;
      const corpo = dd?.body ? (() => { try { return JSON.parse(dd.body); } catch { return dd; } })() : dd;
      if (e?.response?.status === 409 && Array.isArray(corpo?.gemeas) && corpo.gemeas.length && criar) {
        modal.confirm({
          title: 'Essa despesa já está no Contas a Pagar',
          width: 620,
          content: (
            <div>
              <div style={{ marginBottom: 8 }}>Encontrei {corpo.gemeas.length === 1 ? 'esta despesa' : 'estas despesas'} para o mesmo pagamento:</div>
              <ul style={{ paddingLeft: 18, margin: 0 }}>
                {corpo.gemeas.map((g: any) => (
                  <li key={g.id}>{g.descricao} — {fmt(Math.abs(n2(g.valor)))}, {fmtDate(g.vencimento)}{g.paga ? ' (paga)' : ''}</li>
                ))}
              </ul>
              <div style={{ marginTop: 8 }}>
                Registro o pagamento <b>sem criar outra</b>? A que já existe é a que a conciliação vai usar.
                Se ela não for deste pagamento, cancele e confira no Contas a Pagar.
              </div>
            </div>
          ),
          okText: 'Registrar sem criar outra',
          cancelText: 'Voltar',
          onOk: () => salvar(false),
        });
        return;
      }
      message.error(erroDoServidor(e, 'Erro ao registrar o pagamento.'), 10);
    } finally { setSalvando(false); }
  }

  return (
    <Modal
      open
      onCancel={onClose}
      width="94vw"
      style={{ maxWidth: 940, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' } }}
      title={`Registrar pagamento — ${emp.instituicao}${emp.identificador ? ` (${emp.identificador})` : ''}`}
      footer={[
        <Button key="c" onClick={onClose}>Cancelar</Button>,
        <Button key="ok" type="primary" icon={<CheckCircleOutlined />} loading={salvando}
          disabled={!podeSalvar} onClick={() => salvar()}>
          Registrar pagamento de {fmt(pago)}
        </Button>,
      ]}
      destroyOnClose
    >
      <Row gutter={20}>
        <Col xs={24} md={11}>
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <div>
              <Text strong>Parcelas {faixaParcelas(nums)}</Text>
              <Text type="secondary"> ({sel.length} de {emp.prazo_meses})</Text>
              <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                No cronograma: {fmt(nominal)} — principal {fmt(principal)} + juros {fmt(jurosCronograma)}
              </div>
            </div>
            <div>
              <Text style={{ fontSize: 12 }}>Valor que saiu do banco</Text>
              <InputNumber {...PROPS_DINHEIRO_BR} value={valor} onChange={(v) => setValor(v as number | null)}
                min={0} style={{ width: '100%' }} addonBefore="R$" />
              {longeDemais && (
                <Text type="danger" style={{ fontSize: 11 }}>
                  Muito longe do cronograma ({fmt(nominal)}). Confira se faltou ou sobrou um dígito.
                </Text>
              )}
            </div>
            <div>
              <Text style={{ fontSize: 12 }}>Data em que saiu do banco</Text>
              <DatePicker value={data ? dayjs(data) : null} format="DD/MM/YYYY" style={{ width: '100%' }}
                onChange={(d: Dayjs | null) => setData(d ? d.format('YYYY-MM-DD') : '')} />
            </div>
            <div>
              <Text style={{ fontSize: 12 }}>Conta de onde saiu</Text>
              <Select showSearch optionFilterProp="label" placeholder="Escolha a conta" style={{ width: '100%' }}
                value={contaId} onChange={setContaId} allowClear
                options={contas.map(c => ({ value: c.id, label: c.apelido && c.apelido !== c.nome ? `${c.nome} (${c.apelido})` : c.nome }))} />
            </div>
            <div>
              <Text style={{ fontSize: 12 }}>Forma de pagamento (opcional)</Text>
              <Select showSearch optionFilterProp="label" placeholder="Boleto, débito automático…" style={{ width: '100%' }}
                value={forma} onChange={setForma} allowClear
                options={['Boleto', 'Débito automático', 'Pix', 'TED'].map(f => ({ value: f, label: f }))} />
            </div>
            <Checkbox checked={criarDespesa} onChange={(e) => setCriarDespesa(e.target.checked)}>
              Criar a despesa no Contas a Pagar
            </Checkbox>
            {!criarDespesa && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                Desmarque só quando a despesa já existe no Contas a Pagar (veio do Conta Azul) — senão a saída conta duas vezes no caixa.
              </Text>
            )}
          </Space>
        </Col>
        <Col xs={24} md={13}>
          <Card size="small" title="O que vai acontecer" style={{ background: '#fafbff' }}>
            <Space direction="vertical" size={8} style={{ width: '100%', fontSize: 13 }}>
              <div>1. As parcelas {faixaParcelas(nums)} ficam pagas em {fmtDate(data)}, com {fmt(pago)} rateados entre elas.</div>
              <div>
                2. {criarDespesa
                  ? <>Nasce no Contas a Pagar uma despesa <b>paga</b> de <b>{fmt(pago)}</b> em {fmtDate(data)}
                      {conta ? <>, na conta <b>{conta.nome}</b></> : ''}, categoria <b>{cat}</b> — fora do resultado, porque os juros vêm deste módulo.</>
                  : <>Nenhuma despesa é criada no Contas a Pagar.</>}
              </div>
              <div>
                3. {Math.abs(dif) < 0.01
                  ? <>O valor bate com o cronograma: os juros ficam como estão.</>
                  : juros < 0
                    ? <>Desconto de <b>{fmt(-dif)}</b> sobre o cronograma. Os juros dessas parcelas deixam de existir, e{' '}
                        <b>{fmt(-juros)}</b> reduzem os juros das parcelas que faltam: <b>{fmt(-juros / meses)} por mês</b>,
                        de {mesAno(comp)} a {mesAno(fimDesconto)} ({meses} {meses === 1 ? 'mês' : 'meses'}). O resultado não ganha nada de uma vez.</>
                    : dif < 0
                      ? <>Desconto de <b>{fmt(-dif)}</b>: os juros dessas parcelas ficam em <b>{fmt(juros)}</b>, em {mesAno(comp)}.</>
                      : <>Acréscimo de <b>{fmt(dif)}</b> sobre o cronograma (correção da SELIC ou encargos): entra como juros em {mesAno(comp)}.</>}
              </div>
              {criarDespesa && (
                <div>4. No <b>Extrato bancário</b>, a linha do banco passa a sugerir essa despesa (mesmo valor). É só conciliar.</div>
              )}
            </Space>
          </Card>
        </Col>
      </Row>
    </Modal>
  );
}

function ModalParcelas({ emp, onClose, onChanged }: { emp: Emprestimo; onClose: () => void; onChanged: () => void }) {
  const { message, modal } = AntApp.useApp();
  const [parcelas, setParcelas] = useState<Parcela[]>([]);
  const [loading, setLoading] = useState(true);
  const [selecionadas, setSelecionadas] = useState<Key[]>([]);
  const [faixaDe, setFaixaDe] = useState<number | null>(null);
  const [faixaAte, setFaixaAte] = useState<number | null>(null);
  const [pagando, setPagando] = useState<Parcela[] | null>(null);

  useEffect(() => { carregar(); }, []); // eslint-disable-line

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/emprestimos/${emp.id}/parcelas`, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setParcelas(data);
      setSelecionadas([]);
    } finally { setLoading(false); }
  }

  // 🚨 A baixa PERGUNTA a data. O botão antigo carimbava sempre HOJE, e foi por
  // isso que as 20 primeiras parcelas da Mútua ficaram todas com 23/04/2026 —
  // o dia em que o empréstimo foi cadastrado — embora tenham sido pagas entre
  // novembro/2024 e maio/2026. Isto vale para as baixas ANTIGAS (sem valor);
  // pagamento novo passa pelo "Registrar pagamento".
  const [baixando, setBaixando] = useState<{ p: Parcela; data: string } | null>(null);

  async function salvarBaixa() {
    if (!baixando) return;
    try {
      const token = await getToken();
      await axios.patch(`${API_URL}/emprestimos/${emp.id}/parcelas/${baixando.p.numero}`, {
        paga: true, data_pagamento: baixando.data,
      }, { headers: { Authorization: token } });
      message.success(`Parcela ${baixando.p.numero} marcada como paga em ${fmtDate(baixando.data)}.`);
      setBaixando(null);
      await carregar();
      onChanged();
    } catch (e: any) {
      message.error(erroDoServidor(e, 'Erro ao marcar parcela.'), 8);
    }
  }

  async function desfazerBaixa(p: Parcela) {
    try {
      const token = await getToken();
      await axios.patch(`${API_URL}/emprestimos/${emp.id}/parcelas/${p.numero}`, {
        paga: false,
      }, { headers: { Authorization: token } });
      message.success(`Baixa da parcela ${p.numero} desfeita.`);
      await carregar();
      onChanged();
    } catch (e: any) { message.error(erroDoServidor(e, 'Erro ao desfazer a baixa.'), 8); }
  }

  // Desfazer um pagamento registrado devolve TODAS as parcelas dele e tira a
  // despesa do Contas a Pagar — por isso pergunta, dizendo quais.
  function desfazerPagamento(p: Parcela) {
    const faixa = p.pagamento_de && p.pagamento_ate
      ? (p.pagamento_de === p.pagamento_ate ? String(p.pagamento_de) : `${p.pagamento_de} a ${p.pagamento_ate}`)
      : String(p.numero);
    modal.confirm({
      title: 'Desfazer o pagamento?',
      content: `Pagamento de ${fmtDate(p.pagamento_data || p.data_pagamento)} — parcelas ${faixa}, ${fmt(n2(p.pagamento_valor))}. `
        + `As ${p.pagamento_n || 1} parcela(s) voltam a abertas`
        + (p.lancamento_pagar_id ? ' e a despesa sai do Contas a Pagar.' : '.'),
      okText: 'Desfazer',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.patch(`${API_URL}/emprestimos/${emp.id}/parcelas/${p.numero}`, {
            paga: false, desfazer_pagamento: true,
          }, { headers: { Authorization: token } });
          message.success(`Pagamento desfeito: parcelas ${faixa} voltaram a abertas.`);
          await carregar();
          onChanged();
        } catch (e: any) { message.error(erroDoServidor(e, 'Erro ao desfazer o pagamento.'), 10); }
      },
    });
  }

  const abertas = parcelas.filter(p => !p.paga);
  const escolhidas = parcelas.filter(p => selecionadas.includes(p.id));
  const totalEscolhidas = r2(escolhidas.reduce((a, p) => a + n2(p.valor_parcela), 0));

  function selecionarFaixa() {
    if (!faixaDe || !faixaAte) return;
    const [de, ate] = faixaDe <= faixaAte ? [faixaDe, faixaAte] : [faixaAte, faixaDe];
    setSelecionadas(abertas.filter(p => p.numero >= de && p.numero <= ate).map(p => p.id));
  }

  const columns: ColumnsType<Parcela> = [
    { title: '#', dataIndex: 'numero', key: 'n', width: 50, align: 'center', render: (v) => <Text type="secondary">{v}</Text> },
    { title: 'Vencimento', dataIndex: 'data_vencimento', key: 'v', width: 100, align: 'center', render: (v) => <Text type="secondary">{fmtDate(v)}</Text> },
    { title: 'Juros', dataIndex: 'valor_juros', key: 'j', align: 'right', render: (v) => <Text style={{ color: '#B07500' }}>{fmt(parseFloat(v))}</Text> },
    { title: 'Principal', dataIndex: 'valor_principal', key: 'pr', align: 'right', render: (v) => fmt(parseFloat(v)) },
    { title: 'Parcela', dataIndex: 'valor_parcela', key: 'pa', align: 'right', render: (v) => <Text strong>{fmt(parseFloat(v))}</Text> },
    { title: 'Saldo', dataIndex: 'saldo_devedor', key: 's', align: 'right', render: (v) => <Text type="secondary">{fmt(parseFloat(v))}</Text> },
    {
      title: 'Status', key: 'st', width: 380, align: 'center',
      render: (_, r) => r.paga ? (
        <Space size={4}>
          <Tag color="success" icon={<CheckCircleFilled />} style={{ margin: 0 }}>
            Pago {fmtDate(r.data_pagamento)}{r.valor_pago != null ? ` · ${fmt(n2(r.valor_pago))}` : ''}
          </Tag>
          {r.pagamento_id ? (
            // Um botão por PAGAMENTO, na primeira parcela dele: desfazer devolve todas.
            r.numero === r.pagamento_de || !r.pagamento_de ? (
              <Button size="small" type="text" icon={<UndoOutlined />} disabled={!!r.despesa_conciliada}
                title={r.despesa_conciliada
                  ? 'A despesa já está conciliada com o extrato. Desvincule no Extrato bancário para desfazer.'
                  : `Desfazer o pagamento inteiro (parcelas ${r.pagamento_de} a ${r.pagamento_ate})`}
                onClick={() => desfazerPagamento(r)}>
                Desfazer pagamento
              </Button>
            ) : null
          ) : (
            <>
              <Button size="small" type="text" icon={<EditOutlined />} title="Corrigir a data do pagamento"
                onClick={() => setBaixando({ p: r, data: String(r.data_pagamento || r.data_vencimento).slice(0, 10) })} />
              <Button size="small" type="text" icon={<UndoOutlined />} title="Desfazer a baixa"
                onClick={() => desfazerBaixa(r)} />
            </>
          )}
        </Space>
      ) : (
        <Button size="small" icon={<CheckCircleOutlined />} onClick={() => setPagando([r])}>
          Pagar
        </Button>
      ),
    },
  ];

  return (
    <Modal
      open
      onCancel={onClose}
      footer={[<Button key="close" onClick={onClose}>Fechar</Button>]}
      width="94vw"
      style={{ maxWidth: 1100, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' } }}
      title={
        <div>
          <Text strong style={{ color: '#020D7F' }}>Cronograma: {emp.instituicao}</Text>
          <div style={{ fontSize: 11, color: '#888', marginTop: 4, fontWeight: 400 }}>
            {emp.identificador} · {emp.sistema} · {emp.prazo_meses}m a {parseFloat(emp.taxa_mensal_pct).toFixed(3)}%/mês
          </div>
        </div>
      }
      destroyOnClose
    >
      <Flex wrap="wrap" gap={10} align="center" justify="space-between" style={{ marginBottom: 10 }}>
        <Space wrap size={6}>
          <Text style={{ fontSize: 12 }}>Selecionar da parcela</Text>
          <InputNumber size="small" min={1} max={emp.prazo_meses} value={faixaDe} onChange={(v) => setFaixaDe(v as number | null)} style={{ width: 70 }} />
          <Text style={{ fontSize: 12 }}>até</Text>
          <InputNumber size="small" min={1} max={emp.prazo_meses} value={faixaAte} onChange={(v) => setFaixaAte(v as number | null)} style={{ width: 70 }} />
          <Button size="small" onClick={selecionarFaixa} disabled={!faixaDe || !faixaAte}>Selecionar</Button>
        </Space>
        <Space wrap size={8}>
          {escolhidas.length > 0 && (
            <Text style={{ fontSize: 12 }}>
              {escolhidas.length} parcela(s) · {fmt(totalEscolhidas)} no cronograma
            </Text>
          )}
          <Button type="primary" size="small" icon={<CheckCircleOutlined />} disabled={!escolhidas.length}
            onClick={() => setPagando(escolhidas)}>
            Registrar pagamento
          </Button>
        </Space>
      </Flex>
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
        Marque as parcelas que saíram do banco num pagamento só (uma antecipação, por exemplo) e clique em
        Registrar pagamento. A despesa nasce paga no Contas a Pagar, pronta para conciliar com o extrato.
      </Text>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={parcelas}
        loading={loading}
        size="small"
        pagination={false}
        scroll={{ y: 'clamp(240px, calc(100vh - 360px), 720px)' }}
        rowClassName={(r) => r.paga ? 'row-paga' : ''}
        rowSelection={{
          selectedRowKeys: selecionadas,
          onChange: (keys) => setSelecionadas(keys),
          getCheckboxProps: (r) => ({ disabled: r.paga }),
        }}
      />
      {pagando && (
        <ModalRegistrarPagamento
          emp={emp}
          selecionadas={pagando}
          abertasRestantes={abertas.filter(p => !pagando.some(x => x.id === p.id))}
          onClose={() => setPagando(null)}
          onSaved={async () => { setPagando(null); await carregar(); onChanged(); }}
        />
      )}
      <Modal
        open={!!baixando}
        title={baixando ? `Parcela ${baixando.p.numero} — data do pagamento` : ''}
        okText="Confirmar"
        cancelText="Cancelar"
        onCancel={() => setBaixando(null)}
        onOk={salvarBaixa}
        width={380}
        destroyOnClose
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          Em que dia esta parcela saiu do banco? A sugestão é o vencimento
          {baixando ? ` (${fmtDate(baixando.p.data_vencimento)})` : ''} — troque se foi outro dia.
        </Text>
        <DatePicker
          value={baixando?.data ? dayjs(baixando.data) : null}
          onChange={(d: Dayjs | null) => setBaixando(b => b && ({ ...b, data: d ? d.format('YYYY-MM-DD') : '' }))}
          style={{ width: '100%', marginTop: 10 }}
          format="DD/MM/YYYY"
        />
      </Modal>
    </Modal>
  );
}

export default function Emprestimos() {
  return (
    <AntApp>
      <EmprestimosInner />
    </AntApp>
  );
}
